import { splitTenantKey, tenantKey } from './types';
import type { Estate, Memory, MemoryListQuery, MemoryStats, MemoryStatus, MemoryTenant, TenantKey, TenantSummary } from './types';

/** Pure helpers for the memory browser. Every input here is agent-written
 *  tenant content; nothing below parses it as markup or builds a URL from it. */

export type ParsedScope = { kind: 'global' } | { kind: string; id: string };

/** parseScope reads "global" or "<kind>:<id>" leniently: a string that is
 *  neither still renders, as kind "other", and never throws. */
export function parseScope(scope: string): ParsedScope {
  if (scope === 'global') return { kind: 'global' };
  const i = scope.indexOf(':');
  if (i <= 0 || i === scope.length - 1) return { kind: 'other', id: scope };
  return { kind: scope.slice(0, i), id: scope.slice(i + 1) };
}

/** memoryTitle is a memory's own title when it has one (DR-87), else the
 *  first line of its text — trimmed and truncated, for a table cell. Markup
 *  survives verbatim: the cell is a text node. */
export function memoryTitle(m: Pick<Memory, 'text' | 'title'>, max = 120): string {
  const source = m.title?.trim() ? m.title : (m.text ?? '');
  const first = source.split('\n').find((l) => l.trim() !== '') ?? '';
  const line = first.trim();
  if (line.length <= max) return line;
  return `${line.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

export interface SupersessionChain {
  /** Older memories this one replaced, nearest first (walks `supersedes`). */
  ancestors: string[];
  /** Newer memories that replaced this one, nearest first (walks `supersededBy`). */
  descendants: string[];
  /** Ids named by the chain but not loaded on this page. */
  missing: string[];
}

/** supersessionChain walks both directions from `id` over the loaded rows,
 *  cycle-guarded, and reports what it could not follow. */
export function supersessionChain(byId: Record<string, Memory>, id: string): SupersessionChain {
  const out: SupersessionChain = { ancestors: [], descendants: [], missing: [] };
  const seen = new Set<string>([id]);
  const start = byId[id];
  if (!start) {
    out.missing.push(id);
    return out;
  }
  // Ancestors: breadth-first over `supersedes`.
  let frontier = [...(start.supersedes ?? [])];
  while (frontier.length) {
    const next: string[] = [];
    for (const a of frontier) {
      if (seen.has(a)) continue;
      seen.add(a);
      const m = byId[a];
      if (!m) {
        out.missing.push(a);
        continue;
      }
      out.ancestors.push(a);
      next.push(...(m.supersedes ?? []));
    }
    frontier = next;
  }
  // Descendants: follow `supersededBy`.
  let cur = start.supersededBy;
  while (cur) {
    if (seen.has(cur)) break;
    seen.add(cur);
    const m = byId[cur];
    if (!m) {
      out.missing.push(cur);
      break;
    }
    out.descendants.push(cur);
    cur = m.supersededBy;
  }
  return out;
}

export interface MemoryFilter {
  kind: string;
  status: MemoryStatus | '';
  scope: string;
  tag: string;
  text: string;
}

export const defaultMemoryFilter: MemoryFilter = {
  kind: '',
  status: 'active',
  scope: '',
  tag: '',
  text: '',
};

export function memoryFilterDirty(f: MemoryFilter): boolean {
  return (
    f.kind !== defaultMemoryFilter.kind ||
    f.status !== defaultMemoryFilter.status ||
    f.scope.trim() !== '' ||
    f.tag.trim() !== '' ||
    f.text.trim() !== ''
  );
}

/** memoryQuery turns the UI filter into the admin API's query. */
export function memoryQuery(
  estateId: string,
  tenantId: string,
  f: MemoryFilter,
  limit: number,
  cursor?: string,
): MemoryListQuery {
  return {
    estateId,
    tenantId,
    scope: f.scope.trim() || undefined,
    kind: f.kind || undefined,
    status: f.status,
    tag: f.tag.trim() || undefined,
    text: f.text.trim() || undefined,
    limit,
    cursor: cursor || undefined,
  };
}

/** compareMemories orders newest change first, id descending on ties — the
 *  store's own order, so a page renders in the order it was served. */
export function compareMemories(a: Memory, b: Memory): number {
  if (a.updatedAt !== b.updatedAt) return a.updatedAt < b.updatedAt ? 1 : -1;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/** Whether a memory may be forgotten from the browser: only an active one,
 *  and only while the source can write. */
export function canForget(m: Pick<Memory, 'status'> | undefined, live: boolean, busy: boolean): boolean {
  return Boolean(m) && m!.status === 'active' && live && !busy;
}

// ------------------------------------------------- the estate overview (DR-86)

/** The router's per-tenant cap when an estate sets none — the console's
 *  mirror of `types.DefaultMemoryCap`. */
export const DEFAULT_MEMORY_CAP = 10000;

export interface MemoryTotals {
  tenants: number;
  active: number;
  superseded: number;
  deleted: number;
}

/** memoryTenantTotals sums the tenants on screen. It is the estate's total
 *  only when those tenants are the whole estate — see totalsAreWhole. */
export function memoryTenantTotals(rows: MemoryStats[]): MemoryTotals {
  return rows.reduce<MemoryTotals>(
    (t, r) => ({ tenants: t.tenants + 1, active: t.active + r.active, superseded: t.superseded + r.superseded, deleted: t.deleted + r.deleted }),
    { tenants: 0, active: 0, superseded: 0, deleted: 0 },
  );
}

/** A sum is the estate's only when this page is the estate: the first page,
 *  and no cursor came back with it. A later page is a page even when it is
 *  the last one. */
export function totalsAreWhole(page: number, nextCursor: string | undefined): boolean {
  return page === 0 && !nextCursor;
}

export type CapLevel = 'ok' | 'warn' | 'full';

/** memoryCapUse is how much of the per-tenant cap a tenant's active memories
 *  take: warn from 90%, full at the cap, where a remember that would add one
 *  is refused. A cap of 0 or none is the router's default. */
export function memoryCapUse(active: number, cap?: number): { cap: number; fraction: number; level: CapLevel } {
  const c = cap && cap > 0 ? cap : DEFAULT_MEMORY_CAP;
  const fraction = active / c;
  return { cap: c, fraction, level: fraction >= 1 ? 'full' : fraction >= 0.9 ? 'warn' : 'ok' };
}

/** pickMemoryEstate is the estate the overview shows: the scoped tenant's,
 *  then the operator's choice while it names a known estate (or before any
 *  estate is known), then the first with memory on, then the first. */
export function pickMemoryEstate(estates: Pick<Estate, 'id' | 'memory'>[], scope: TenantKey | null, chosen: string): string {
  if (scope) return splitTenantKey(scope).estateId;
  if (chosen && (estates.length === 0 || estates.some((e) => e.id === chosen))) return chosen;
  return (estates.find((e) => e.memory?.mode === 'on') ?? estates[0])?.id ?? '';
}

/** The overview's cursor stack, tagged with the estate whose cursors it
 *  holds. A cursor is a tenant id in ONE estate; sent for another it would
 *  silently skip every tenant sorting before it. */
export interface TenantNav {
  estate: string;
  cursors: string[];
  page: number;
}

export const emptyTenantNav: TenantNav = { estate: '', cursors: [], page: 0 };

/** tenantNavFor is the paging for `estate`: the stored stack when it is that
 *  estate's, page one otherwise — however the estate changed (the rail, the
 *  header's scope, estates arriving after the first render). */
export function tenantNavFor(nav: TenantNav, estate: string): TenantNav {
  return nav.estate === estate ? nav : { estate, cursors: [], page: 0 };
}

/** The cursor that opens the current page of `estate`; "" for page one. */
export function tenantNavCursor(nav: TenantNav, estate: string): string {
  const n = tenantNavFor(nav, estate);
  return n.page > 0 ? (n.cursors[n.page - 1] ?? '') : '';
}

export function nextTenantNav(nav: TenantNav, estate: string, cursor: string): TenantNav {
  const n = tenantNavFor(nav, estate);
  return { estate, cursors: [...n.cursors.slice(0, n.page), cursor], page: n.page + 1 };
}

export function prevTenantNav(nav: TenantNav, estate: string): TenantNav {
  const n = tenantNavFor(nav, estate);
  return { ...n, page: Math.max(0, n.page - 1) };
}

export interface MemoryTenantRow extends MemoryTenant {
  key: TenantKey;
  /** The registry's display name; absent when the registry has not seen the
   *  tenant — it is filled by the accounting writer, so a tenant whose usage
   *  has not reached the warehouse holds memories before it is registered. */
  displayName?: string;
}

/** memoryTenantRows keeps only `estate`'s rows — a load still in flight
 *  leaves the previous estate's rows in the store, and they must not render
 *  under this estate's policy — and names each from the registry by the full
 *  (estate, tenant) key, never the tenant id alone. */
export function memoryTenantRows(
  items: MemoryTenant[],
  registry: Pick<TenantSummary, 'key' | 'displayName'>[],
  estate: string,
): MemoryTenantRow[] {
  const names = new Map(registry.map((t) => [t.key, t.displayName]));
  return items
    .filter((t) => t.estateId === estate)
    .map((t) => {
      const key = tenantKey(t.estateId, t.tenantId);
      return { ...t, key, displayName: names.get(key) };
    });
}

/** The tenant browser's path, both segments encoded: a tenant id is the
 *  caller's string and may hold a slash or a question mark. */
export function memoryBrowserPath(estateId: string, tenantId: string): string {
  return `/estates/${encodeURIComponent(estateId)}/tenants/${encodeURIComponent(tenantId)}/memory`;
}
